import React, { useEffect, useState } from 'react';
import { Modal, Button, Avatar, Typography, Space, Tag } from 'antd';
import { PhoneOutlined, PhoneFilled, CloseOutlined, UserOutlined } from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import pusherService from '../../services/pusher';
import soundNotificationService from '../../services/soundNotification';

const { Text } = Typography;

function IncomingCallOverlay() {
    const { user } = useAuth();
    const [visible, setVisible] = useState(false);
    const [payload, setPayload] = useState(null);

    useEffect(() => {
        if (!user) return;
        let userChannel = null;
        let vendorChannel = null;

        (async () => {
            try {
                userChannel = await pusherService.subscribeToUser(user.id);
                if (userChannel) {
                    userChannel.bind('booking-updated', onBookingUpdated);
                }
                if (user?.user_type === 'vendor' && user.vendor?.id) {
                    vendorChannel = await pusherService.subscribeToVendor(user.vendor.id);
                    if (vendorChannel) {
                        vendorChannel.bind('new-lead', onNewLead);
                    }
                }
            } catch (_) {}
        })();

        return () => {
            try { userChannel && userChannel.unbind_all && userChannel.unbind_all(); } catch(_) {}
            try { vendorChannel && vendorChannel.unbind_all && vendorChannel.unbind_all(); } catch(_) {}
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    const onNewLead = (data) => {
        if (visible) return; // prevent overlapping ringers
        setPayload({ type: 'lead', lead: data?.lead });
        setVisible(true);
        try { soundNotificationService.playSirenSound(); } catch(_) {}
    };

    const onBookingUpdated = (data) => {
        // Show for key booking/order events
        const status = (data?.booking?.status || '').toString().toLowerCase();
        const ringStatuses = new Set(['confirmed', 'accepted', 'paid', 'preparing', 'ready']);
        if (!visible && status && ringStatuses.has(status)) {
            setPayload({ type: 'booking', booking: data.booking });
            setVisible(true);
            try { soundNotificationService.playSirenSound(); } catch(_) {}
        }
    };

    const handleAccept = () => {
        if (payload?.type === 'lead' && payload.lead?.id) {
            window.location.href = `/chat/${payload.lead.id}`;
        } else if (payload?.type === 'booking' && payload.booking?.id) {
            window.location.href = `/bookings/${payload.booking.id}`;
        }
        setVisible(false);
        setPayload(null);
    };

    const handleDismiss = () => {
        setVisible(false);
        setPayload(null);
    };

    if (!visible || !payload) return null;

    const title = payload.type === 'lead' ? 'New Booking Request' : 'Booking Update';
    const name = payload.type === 'lead'
        ? (payload.lead?.user?.name || 'Customer')
        : (payload.booking?.vendor?.business_name || 'Vendor');
    const context = payload.type === 'lead'
        ? `${payload.lead?.event_type || ''} • ${payload.lead?.guest_count || ''} guests`
        : (payload.booking?.status || '').toString().toUpperCase();

    return (
        <Modal
            open={visible}
            footer={null}
            closable={false}
            width={420}
            centered
            className="incoming-call-modal"
            styles={{ body: { padding: 0 } }}
        >
            <div className="bg-gradient-to-br from-indigo-600 via-blue-600 to-purple-600 text-white p-6 rounded-t-lg">
                <div className="flex items-center gap-3">
                    <PhoneFilled className="text-2xl" />
                    <div className="text-lg font-semibold">{title}</div>
                </div>
            </div>
            <div className="p-6 bg-white">
                <div className="flex flex-col items-center text-center">
                    <Avatar size={72} icon={<UserOutlined />} className="mb-3" />
                    <Text strong className="text-base">{name}</Text>
                    <div className="mt-1">
                        <Tag color="blue">{context}</Tag>
                    </div>
                    {payload.type === 'lead' && (
                        <div className="text-gray-500 text-xs mt-1">
                            {payload.lead?.event_date} {payload.lead?.event_time} • {payload.lead?.venue}
                        </div>
                    )}
                </div>

                <Space className="w-full mt-5" align="center" style={{ justifyContent: 'center' }}>
                    <Button
                        danger
                        type="primary"
                        icon={<CloseOutlined />}
                        onClick={handleDismiss}
                        className="rounded-full h-11 px-6"
                    >
                        Dismiss
                    </Button>
                    <Button
                        type="primary"
                        icon={<PhoneOutlined />}
                        onClick={handleAccept}
                        className="rounded-full h-11 px-6"
                    >
                        View
                    </Button>
                </Space>
            </div>
        </Modal>
    );
}

export default IncomingCallOverlay;


